import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  CreateWorkflowNodeFileDocument,
  type CreateWorkflowNodeFileMutation,
  FindWorkflowDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/procurement/components/DropItem'
import { ApproverSelectionContainer } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/ApproverSelectionContainer'
import { DownloadDocumentHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/downloadDocumentHandler'
import { StrategyItem } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/interfaces'
import {
  strategiesFromPayload,
  strategiesToPayload,
} from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/utils/strategyUtils'
import {
  HandlerFormComponent,
  useWorkflowContext,
} from 'modules/workflows/contexts/WorkflowContext'
import { getDurationUnitOptions } from 'modules/workflows/lib/durationUnit'
import { mapWorkflowNodeFilesToNodes } from 'modules/workflows/lib/mapWorkflowNodeFilesToNodes'

interface FileItem {
  file: File
  id: string
  blobUrl: string
}

export const DownloadDocumentHandlerForm = ({}: HandlerFormComponent) => {
  const { control, watch, setValue, getValues, trigger } =
    useFormContext<DownloadDocumentHandlerSchema>()
  const { workflow, nodesUI } = useWorkflowContext()
  const { company } = useCurrentSession()

  // Get selected node to determine if it's persisted or local
  const selectedNode = useMemo(() => nodesUI.find((node) => node.selected), [nodesUI])

  // Mutation to create workflow node files
  const [createWorkflowNodeFile, { loading: isUploading }] = useMutation(
    CreateWorkflowNodeFileDocument,
    {
      extractorFn: (x: CreateWorkflowNodeFileMutation) =>
        x.workflowsMutations?.createWorkflowsWorkflowNodeFile,
      refetchQueries: [FindWorkflowDocument],
    }
  )

  const payload = watch('nodeData.payload')

  const { strategies, fallbackStrategies } = useMemo(() => {
    if (!payload) {
      return {
        strategies: [],
        fallbackStrategies: [],
      }
    }
    return strategiesFromPayload<'strategies' | 'fallbackStrategies'>([
      {
        type: 'ids',
        data: payload.employee_ids,
        groupAs: 'strategies',
      },
      {
        type: 'ids',
        data: payload.fallback_employee_ids,
        groupAs: 'fallbackStrategies',
      },
      {
        type: 'segments',
        data: payload.employee_segments,
        groupAs: 'strategies',
      },
      {
        type: 'segments',
        data: payload.fallback_employee_segments,
        groupAs: 'fallbackStrategies',
      },
    ])
  }, [payload])

  const handleMutateStrategies = useCallback(
    (data: { add?: StrategyItem[]; fallbackAdd?: StrategyItem[]; remove?: string }) => {
      if (!data.add?.length && !data.fallbackAdd?.length && !data.remove) {
        return
      }

      const { add = [], fallbackAdd = [], remove } = data

      const allStrategies = [...strategies, ...add].filter((strategy) => remove !== strategy.id)
      const allFallbackStrategies = [...fallbackStrategies, ...fallbackAdd].filter(
        (strategy) => remove !== strategy.id
      )

      const currentPayload = getValues().nodeData.payload

      const newPayload = strategiesToPayload<typeof payload>(
        [
          {
            field: 'employee_ids',
            type: 'ids',
            data: allStrategies,
          },
          {
            field: 'employee_segments',
            type: 'segments',
            data: allStrategies,
          },
          {
            field: 'fallback_employee_ids',
            type: 'ids',
            data: allFallbackStrategies,
          },
          {
            field: 'fallback_employee_segments',
            type: 'segments',
            data: allFallbackStrategies,
          },
        ],
        currentPayload
      )

      setValue('nodeData.payload', newPayload, { shouldValidate: true, shouldDirty: true })
    },
    [strategies, fallbackStrategies, getValues, setValue]
  )

  const unselectableEmployees = useMemo(
    () => [payload?.employee_ids, payload?.fallback_employee_ids].flat().filter(Boolean),
    [payload?.employee_ids, payload?.fallback_employee_ids]
  )

  const unselectableTeams = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && ['anyone_in_team', 'team_leads'].includes(segment.name))
        .map(
          (segment) => segment && ('team_id' in segment.input ? segment.input.team_id : undefined)
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableRoles = useMemo(
    () =>
      [payload?.employee_segments, payload?.fallback_employee_segments]
        .flat()
        .filter((segment) => segment && segment.name === 'job_catalog_role')
        .map((segment) =>
          segment && 'role_id' in segment.input ? segment.input.role_id : undefined
        )
        .filter(Boolean),
    [payload?.employee_segments, payload?.fallback_employee_segments]
  )

  const unselectableStrategies = useMemo(
    () =>
      [strategies, fallbackStrategies]
        .flat()
        .filter((strategy) =>
          ['employee_manager', 'admins', 'resource_owner'].includes(strategy.type)
        )
        .map((strategy) => strategy.type),
    [strategies, fallbackStrategies]
  )

  const filesFromPayload = watch('nodeData.payload.files') ?? []

  // Convert File objects to a format compatible with DropItem
  const [localFiles, setLocalFiles] = useState<FileItem[]>(() => {
    // Initialize with files from payload if they exist
    return filesFromPayload.map((file, index) => {
      const id = `local-${index}-${Date.now()}`
      return {
        file,
        id,
        blobUrl: URL.createObjectURL(file),
      }
    })
  })

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      localFiles.forEach((fileItem) => {
        URL.revokeObjectURL(fileItem.blobUrl)
      })
    }
  }, [localFiles])

  // Sync local files to form payload
  useEffect(() => {
    const files = localFiles.map((fileItem) => fileItem.file)
    setValue('nodeData.payload.files', files)
    trigger('nodeData.payload.files')
  }, [localFiles, setValue, trigger])

  const handleOnDrop = useUnsafeCallback(
    async (acceptedFiles: File[]) => {
      if (!company?.id || !workflow?.id || !selectedNode) {
        return
      }

      try {
        // Determine if node is persisted (has numeric ID) or local (has string identifier)
        const workflowNodeId = selectedNode.id.match(/^\d+$/) ? Number(selectedNode.id) : null

        const result = await createWorkflowNodeFile({
          variables: {
            workflowId: workflow.id,
            workflowNodeId: workflowNodeId ?? undefined,
            // Use the UI node id as identifier for associating files to the node
            nodeIdentifier: selectedNode.id,
            files: acceptedFiles,
          },
        })

        const handled = handleResult(result, (errors) => {
          const errorMessages = Object.values(errors).join(', ')
          app.setFlash({
            type: 'wrong',
            message: `Failed to upload files: ${errorMessages}`,
            time: 3000,
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
            time: 3000,
          })
        }
      } catch (error) {
        app.setFlash({
          type: 'wrong',
          message: `Failed to upload files: ${String(error)}`,
          time: 3000,
        })
      }
    },
    [company?.id, workflow?.id, selectedNode, createWorkflowNodeFile]
  )

  const handleFileRemove = useUnsafeCallback((fileId: string) => {
    setLocalFiles((prev) => {
      const fileToRemove = prev.find((item) => item.id === fileId)
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.blobUrl)
      }
      return prev.filter((item) => item.id !== fileId)
    })
  }, [])

  // Map workflow node files to nodes and get files for the selected node
  const workflowNodeFilesByNode = useMemo(() => {
    if (!workflow?.workflowNodeFiles) {
      return new Map<string, (typeof workflow.workflowNodeFiles)[number][]>()
    }

    return mapWorkflowNodeFilesToNodes(workflow.workflowNodeFiles, workflow.workflowNodes)
  }, [workflow])

  const workflowNodeFiles = useMemo(
    () => (selectedNode ? (workflowNodeFilesByNode.get(selectedNode.id) ?? []) : []),
    [selectedNode, workflowNodeFilesByNode]
  )

  // Sync workflow node files to form payload
  useEffect(() => {
    const workflowNodeFilesIds = workflowNodeFiles.map((file) => file.id)
    setValue('nodeData.payload.workflow_node_files_ids', workflowNodeFilesIds)
    trigger('nodeData.payload.workflow_node_files_ids')
  }, [workflowNodeFiles, setValue, trigger])

  // Convert FileItem objects to ProcurementFile-like format for DropItem
  const localFileItems = localFiles.map((fileItem) => ({
    __typename: 'ProcurementFile' as const,
    id: Number(fileItem.id.replace(/\D/g, '') || Date.now()),
    filename: fileItem.file.name,
    url: fileItem.blobUrl,
    contentType: fileItem.file.type || '',
    byteSize: fileItem.file.size,
  }))

  // Convert WorkflowNodeFiles to ProcurementFile-like format for DropItem
  const workflowFileItems = workflowNodeFiles
    .filter((file) => file.file)
    .map((file) => {
      const fileData = file.file
      if (!fileData) return null
      return {
        __typename: 'ProcurementFile' as const,
        id: file.id,
        filename: fileData.filename,
        url: fileData.url,
        contentType: fileData.contentType || '',
        byteSize: 0, // Not available in WorkflowNodeFile
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  // Combine local files and workflow files
  const allFileItems = [...workflowFileItems, ...localFileItems]

  return (
    <Box gap="s16">
      {/* Task Title */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.title"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              size="md"
              label={i18n.t('workflows.sidepanel.download_document.title_label')}
              placeholder={i18n.t('workflows.sidepanel.download_document.title_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Task Description */}
      <Box gap="s8" width="full">
        <Controller
          name="nodeData.payload.description"
          control={control}
          render={({ field, fieldState }) => (
            <Textarea
              label={i18n.t('workflows.sidepanel.download_document.description_label')}
              placeholder={i18n.t('workflows.sidepanel.download_document.description_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Assignees */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.download_document.assignees_label')}
        </Text>
        <ApproverSelectionContainer
          entityLabel={i18n.t('workflows.sidepanel.approval.entities.assignee')}
          newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_assignee')}
          placeholder={i18n.t('workflows.sidepanel.approval.fields.new_assignee_placeholder')}
          onAddStrategies={(data) => handleMutateStrategies({ add: data })}
          onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
          strategies={strategies}
          unselectableEmployees={unselectableEmployees}
          unselectableRoles={unselectableRoles}
          unselectableStrategies={unselectableStrategies}
          unselectableTeams={unselectableTeams}
        />
      </Box>

      {/* Fallback Assignees */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.approval.fields.fallback_assignee')}
        </Text>
        <ApproverSelectionContainer
          entityLabel={i18n.t('workflows.sidepanel.approval.entities.fallback_assignee')}
          newEntityLabel={i18n.t('workflows.sidepanel.approval.fields.new_fallback_assignee')}
          placeholder={i18n.t(
            'workflows.sidepanel.approval.fields.new_fallback_assignee_placeholder'
          )}
          maxStrategies={1}
          onAddStrategies={(data) => handleMutateStrategies({ fallbackAdd: data })}
          onDeleteStrategy={(id) => handleMutateStrategies({ remove: id })}
          strategies={fallbackStrategies}
          unselectableEmployees={unselectableEmployees}
          unselectableRoles={unselectableRoles}
          unselectableStrategies={unselectableStrategies}
          unselectableTeams={unselectableTeams}
        />
      </Box>

      {/* Files Dropzone */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.download_document.files_label')}
        </Text>
        <DropZone
          onDrop={handleOnDrop}
          accept={{ 'application/pdf': [], 'image/jpeg': [], 'image/png': [] }}
          multiple
          placeholder={i18n.t('workflows.sidepanel.download_document.dropzone_placeholder')}
          disabled={isUploading}
        />
      </Box>

      {/* Files List */}
      {allFileItems.length > 0 && (
        <Box flexDirection="column" gap="s8" width="full">
          {allFileItems.map((file) => {
            // Only allow removal of local files (not workflow files)
            const isLocalFile = localFileItems.some((localFile) => localFile.id === file.id)
            return (
              <DropItem
                key={file.id}
                file={file}
                onRemove={isLocalFile ? () => handleFileRemove(String(file.id)) : undefined}
              />
            )
          })}
        </Box>
      )}

      {/* Due On */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.download_document.due_on_label')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="baseline">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label={i18n.t('workflows.sidepanel.download_document.due_on_label')}
                  hideLabel
                  placeholder="2"
                  value={String(field.value)}
                  onChange={(value) => field.onChange(Number(value))}
                  error={fieldState.error?.message}
                  type="number"
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label={i18n.t('workflows.sidepanel.download_document.due_on_label')}
                  hideLabel
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  options={getDurationUnitOptions()}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
